import React, { useCallback, useEffect, useRef } from 'react';
import { Chip } from '@landr/maestro';
import { css } from '@landr/styled-system/css';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { useSuggestionsPillsContext } from '../context/SuggestionsPillsContext';

const chipClass = css({
    cursor: 'click',
    border: 'high',

    _hover: {
        backgroundColor: 'surface.neutral.medium.hover',
    },
});

const useSuggestion = ({ modifiers }: { modifiers: ModifiersPayload }) => {
    const pillRef = useRef<HTMLDivElement>(null);

    const { getFilterLabel } = useApiFiltersContext();
    const { hasOverflow, getIsOverlappingPill, registerPill } =
        useSuggestionsPillsContext();

    const [filter, slug] = modifiers;
    const pillId = `${filter}-${slug}`;

    // Register pill with context
    useEffect(() => {
        if (pillRef.current) {
            registerPill(pillId, pillRef);
        }
    }, [pillId, registerPill]);

    const isOverlapping = getIsOverlappingPill(pillId);

    const displayName = getFilterLabel(slug ?? '', filter).toLowerCase();
    const { addFilter } = useSearchModifiers();
    const shouldHide = isOverlapping && hasOverflow;

    const handleClick = useCallback(() => {
        addFilter([modifiers]);
    }, [addFilter, modifiers]);

    const visibility: React.CSSProperties['visibility'] = shouldHide
        ? 'hidden'
        : 'visible';

    return {
        pillRef,
        isOverlapping,
        displayName,
        visibility,
        handleClick,
        filter,
        slug,
    };
};

export const Suggestion = ({ modifiers }: { modifiers: ModifiersPayload }) => {
    const { pillRef, visibility, displayName, handleClick, slug } =
        useSuggestion({ modifiers });

    if (!slug) {
        return null;
    }

    return (
        <Chip
            ref={pillRef}
            isRounded
            size="sm"
            onClick={handleClick}
            className={chipClass}
            style={{
                visibility: visibility,
            }}
        >
            {displayName}
        </Chip>
    );
};
